import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { getSessionId } from '@fs/zion-session'
import { Paragraph, WhiteTheme, Image, Grid, Row, DataPreview } from '@fs/zion-ui'
import { ArrowChange, MediaCamera } from '@fs/zion-icon'
import { Affordance, usePageWidth } from '@fs/zion-ui/quarks-authorized-use-only'

const wrapperCss = css`
  & img {
    animation: unset;
    border-radius: inherit;
  }
`

function AffordanceWrapper({ isAdmin, isAutoWidth, children, handleImageClick, groupId, style = {} }) {
  const commonStyles = {
    width: '370px',
    height: '278px',
    borderRadius: 'inherit',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }
  const extraStyles = isAutoWidth ? { width: 'auto', maxWidth: '370px' } : {}
  if (isAdmin) {
    return (
      <Affordance
        // className={cx(isHoverableCss, wrapperCss)}
        tabIndex={0}
        onClick={handleImageClick}
        data-group-id={groupId}
        margin="none"
        style={{
          margin: '0 auto',
          ...commonStyles,
          ...extraStyles,
          ...style,
        }}
      >
        {children}
      </Affordance>
    )
  }
  return (
    <div className={wrapperCss} style={{ ...commonStyles, ...extraStyles, ...style }}>
      {children}
    </div>
  )
}

export default function GroupImage({ group, onGroupImageClick, isMobile, inGroupList, isAdmin }) {
  const [t] = useTranslation()
  const atWidth = usePageWidth()
  const isSuperSmall = atWidth({ default: true, xs: false })
  const [isGroupAdmin, setIsGroupAdmin] = useState(isAdmin || group?.isFallback) // If the group is a fallback, then the user is creating a group and should be treated as the admin
  const [isImagePending, setIsImagePending] = useState(group?.imagePending ?? false)
  const imageUrl = useMemo(() => {
    if (!group?.imageUrl) return ''
    return group.isFallback ? group.imageUrl : `${group.imageUrl}?sessionId=${getSessionId()}`
  }, [group])

  const isCurrent = useRef(true)
  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  // Set imagePending
  useEffect(() => {
    setIsImagePending(!!group?.imagePending)
  }, [group?.imagePending])

  useEffect(() => setIsGroupAdmin(isAdmin || group?.isFallback), [group?.isFallback, isAdmin])

  const handleImageClick = useCallback(
    (event) => {
      if (isGroupAdmin && onGroupImageClick && (event.key === 'Enter' || !event.key)) {
        onGroupImageClick()
      }
    },
    [isGroupAdmin, onGroupImageClick]
  )

  const handleImageError = useCallback(() => setIsImagePending(true), [])

  let backgroundColor = 'unset'
  if (!imageUrl) {
    if (isImagePending) backgroundColor = '#545659'
    else backgroundColor = '#DDDFDF'
  }
  const widthStyle = isSuperSmall && !inGroupList ? { width: '280px', height: '200px' } : {}

  return (
    <>
      <DataPreview.Loading loading={!group}>
        <AffordanceWrapper
          isAutoWidth={isSuperSmall || (isMobile && inGroupList)}
          style={{ backgroundColor, borderRadius: inGroupList ? 'unset' : '8px', ...widthStyle }}
        >
          <div />
        </AffordanceWrapper>
      </DataPreview.Loading>

      <DataPreview.Ready loading={!group}>
        <AffordanceWrapper
          handleImageClick={handleImageClick}
          groupId={group?.id}
          isAdmin={isGroupAdmin}
          isAutoWidth={isSuperSmall || (isMobile && inGroupList)}
          style={{ backgroundColor, borderRadius: inGroupList ? 'unset' : '8px', ...widthStyle }}
        >
          {imageUrl ? (
            <Image
              minHeight={inGroupList ? '278px' : undefined}
              src={imageUrl}
              alt={t('group-image.alt-text', 'group image')}
              // @ts-expect-error -- zion-ui's Image does pass onError but does not state it in its types.
              onError={handleImageError}
            />
          ) : (
            <>
              {isImagePending ? (
                <Grid guttersY="none">
                  <Row alignX="center">
                    <ArrowChange size="lg" color="white" />
                  </Row>
                  <WhiteTheme>
                    <Paragraph centered>{t('group-image.image-processing', 'Image Processing')}</Paragraph>
                  </WhiteTheme>
                </Grid>
              ) : (
                <MediaCamera size="lg" />
              )}
            </>
          )}
        </AffordanceWrapper>
      </DataPreview.Ready>
    </>
  )
}
